import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '../hooks/useTheme'
import { useTranslation } from '../i18n'
import { useKeyboardVisible } from '../hooks/useKeyboardVisible'
import { useAuth } from '../contexts/AuthContext'

// Lucide-style SVG paths — inline to avoid adding a dependency
const icons = {
  home: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  ),
  calendar: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  activity: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  fileText: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  blocks: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="5" rx="2" />
      <rect x="2" y="9.5" width="20" height="5" rx="2" />
      <rect x="2" y="17" width="20" height="5" rx="2" />
    </svg>
  ),
  programs: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  more: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
}

export function BottomNavigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const isKeyboardVisible = useKeyboardVisible()

  const isCoach = user?.hasTrainees ?? false
  const hasCoaches = user?.hasCoaches ?? false

  const navItems = isCoach
    ? [
        { path: '/', label: t.navigation.home, icon: icons.home, tourId: 'tour-nav-home' },
        { path: '/this-week-trainings', label: t.navigation.weekly, icon: icons.calendar, tourId: 'tour-nav-schedule' },
        { path: '/all-training-sets', label: t.navigation.sets, icon: icons.fileText, tourId: 'tour-nav-sets' },
        { path: '/more', label: t.navigation.more, icon: icons.more, tourId: 'tour-nav-more' },
      ]
    : hasCoaches
      ? [
          { path: '/', label: t.navigation.home, icon: icons.home, tourId: 'tour-nav-home' },
          { path: '/this-week-trainings', label: t.navigation.weekly, icon: icons.calendar, tourId: 'tour-nav-schedule' },
          { path: '/active-training-set', label: t.quickAccess.items['active-training-set'], icon: icons.fileText, tourId: 'tour-nav-sets' },
          { path: '/more', label: t.navigation.more, icon: icons.more, tourId: 'tour-nav-more' },
        ]
      : [
          { path: '/', label: t.navigation.home, icon: icons.home, tourId: 'tour-nav-home' },
          { path: '/training-blocks', label: t.navigation.blocks, icon: icons.blocks, tourId: 'tour-nav-blocks' },
          { path: '/training-programs', label: t.navigation.programs, icon: icons.programs, tourId: 'tour-nav-programs' },
          { path: '/more', label: t.navigation.more, icon: icons.more, tourId: 'tour-nav-more' },
        ]

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 border-t transition-transform duration-300 ease-in-out"
      aria-hidden={isKeyboardVisible}
      style={{
        backgroundColor: theme.bgColor,
        borderTopColor: theme.cardBorder,
        paddingBottom: `calc(0.5rem + env(safe-area-inset-bottom, 0px))`,
        paddingTop: '0.5rem',
        boxShadow: '0 -1px 8px rgba(0,0,0,0.08)',
        transform: isKeyboardVisible ? 'translateY(100%)' : 'translateY(0)',
        // Do not use visibility:hidden here: iOS WebKit can fail to paint a
        // position:fixed bar again after the keyboard closes until reload.
        pointerEvents: isKeyboardVisible ? 'none' : 'auto',
      }}
    >
      <div className="flex items-center justify-around h-14 max-w-screen-lg mx-auto">
        {navItems.map(item => {
          const isActive = location.pathname === item.path
            || (item.path === '/active-training-set' && /^\/trainee\/\d+\/set\//.test(location.pathname))
          return (
            <button
              key={item.label}
              data-tour={item.tourId}
              onClick={() => navigate(item.path!)}
              className="flex flex-col items-center justify-center flex-1 h-full min-w-0 transition-all active:scale-95 px-1 relative"
              style={{ color: isActive ? theme.linkColor : theme.hintColor }}
            >
              <span className="mb-0.5">{item.icon}</span>
              <span className="text-[10px] font-semibold truncate w-full text-center">{item.label}</span>
              {isActive && (
                <span
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-sm"
                  style={{ width: 16, height: 3, backgroundColor: theme.linkColor, borderRadius: 2 }}
                />
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
